import React from 'react';
import { motion } from 'framer-motion';

const LuxuryShoppingGuiltLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Luxury Item Guilt: Is That Designer Purchase Worth The Emotional Cost?
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Calculate Your Luxury Purchase Guilt</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Track premium purchases, understand your high-end shopping motivations, and find balance between luxury indulgence and financial peace of mind.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Luxury Purchase
            </motion.button>
          </div>
          <div className="text-8xl">💎</div>
        </div>
      </div>
      
      {/* What causes luxury shopping guilt section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Do We Feel Guilty About Luxury Purchases?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💸</span>
              <div>
                <h3 className="font-bold dark:text-white">Financial Concerns</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Premium prices trigger anxiety about financial responsibility and impact on long-term savings goals.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🏆</span>
              <div>
                <h3 className="font-bold dark:text-white">Status Seeking</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Buying for social status rather than genuine appreciation leads to post-purchase cognitive dissonance.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⚖️</span>
              <div>
                <h3 className="font-bold dark:text-white">Value Justification</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Struggling to rationalize the premium price compared to more affordable alternatives.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🌍</span>
              <div>
                <h3 className="font-bold dark:text-white">Privilege Awareness</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Consciousness of global inequality and ethical concerns about conspicuous consumption.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Guilt Gauge helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Luxury Guilt Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Measure True Value
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Calculate cost-per-use and long-term value to determine if premium purchases are truly worth it.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Identify Purchasing Motivations
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Understand if you're buying for quality, status, emotional satisfaction, or investment potential.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Balance Spending with Responsibility
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Develop a personalized framework for sustainable luxury consumption that aligns with your finances.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Make Peace with Premium Purchases
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Reduce cognitive dissonance with thoughtful pre-purchase planning and post-purchase reflection.
            </p>
          </div>
        </div>
      </section>
      
      {/* Luxury Buyer Types */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Luxury Buyer Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">✨</span>
              <h3 className="font-bold mt-2 dark:text-white">Quality Connoisseur</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You invest in craftsmanship and materials, appreciating the artistry and durability of premium goods.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🏷️</span>
              <h3 className="font-bold mt-2 dark:text-white">Label Lover</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You're drawn to designer names and status symbols, often experiencing guilt about prioritizing prestige.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🧠</span>
              <h3 className="font-bold mt-2 dark:text-white">Deliberate Luxurist</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You carefully research and save for select high-end pieces, blending luxury with financial responsibility.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎭</span>
              <h3 className="font-bold mt-2 dark:text-white">Aspirational Buyer</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You stretch your budget for premium items, sometimes experiencing financial anxiety afterward.
            </p>
          </div>
        </div>
      </section>
      
      {/* Luxury Guild Statistics */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Luxury Purchase Insights</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">67%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of luxury buyers experience guilt post-purchase</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">22x</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average markup on luxury goods vs. production cost</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">43%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">hide luxury purchases from partners or family</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$4,800</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average annual spend on premium items</p>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">From Guilt to Gratification: Success Stories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I used to impulse-buy designer bags then feel terrible about the expense. After tracking my purchases, I realized quality investment pieces bring me joy without guilt when I plan for them."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Sophia R.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Reformed Impulse Luxurist</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I created a 'luxury fund' where I save specifically for high-end purchases. When I finally buy something premium, I enjoy it fully because I know it's financially responsible."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Marcus T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Deliberate Luxurist</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Luxury Shopping Guilt: Common Questions</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is it wrong to buy luxury items?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              There's nothing inherently wrong with purchasing luxury goods when they align with your values and financial situation. The key is ensuring premium purchases are intentional, meaningful, and don't compromise your financial wellbeing or create lasting guilt.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I justify spending so much on one item?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Consider the cost-per-use, longevity, craftsmanship, and the genuine joy it brings you. Our calculator helps measure these factors against financial impact. For investment pieces, track whether they truly deliver the long-term value and satisfaction that justifies their premium.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How much is too much to spend on luxury?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Financial advisors often recommend limiting discretionary luxury spending to 5-10% of your income after essential expenses and savings goals are met. Our calculator helps you determine your personal comfort threshold based on your unique financial situation and emotional response.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I stop feeling guilty about expensive purchases?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Create a deliberate luxury plan that includes saving specifically for premium items, researching thoroughly before buying, ensuring purchases align with your values, and tracking your emotional response. Our tool helps you develop a personalized framework for guilt-free luxury consumption.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready for Guilt-Free Luxury?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Track your premium purchases and discover how to balance indulgence with financial peace of mind.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Luxury Guilt Now
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Understanding Luxury Purchase Guilt</h2>
          <p className="mb-3">
            Luxury purchase guilt is the emotional conflict experienced after buying premium items, stemming from concerns about financial responsibility, value justification, status-seeking behavior, and privilege awareness in a world of inequality.
          </p>
          <p className="mb-3">
            Our Luxury Guilt Calculator helps you understand and address these feelings by measuring the true value of high-end purchases, identifying your emotional motivations, and developing a personalized framework for sustainable luxury consumption.
          </p>
          <p>
            Whether you're struggling with designer remorse, questioning the value of premium brands, or seeking to balance quality indulgence with financial wellness, our free tool helps you make peace with your luxury purchases and develop healthier high-end shopping habits.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default LuxuryShoppingGuiltLanding;
